import {AxiosInstance} from 'axios';
import {authFetch} from '../auth/session';
import type {AddressSearchResult, PlanDocuments, PlanMetadata, PropertyContext} from './types';

/** Callbacks for any property-analysis SSE stream. Every pipeline entry point
 *  emits the same event vocabulary, so analyze and resume share this. */
export interface PipelineStreamCallbacks {
    onProgress?: (event: any) => void;
    onPlanComplete?: (event: {plannavn: string; doc_id: string}) => void;
    onPlanSkipped?: (event: {plannavn: string; doc_id: string | null; reason: string}) => void;
    onPlanFailed?: (event: {plannavn: string; error: string}) => void;
    onDiscoveryProgress?: (event: any) => void;
    onDiscoveryResult?: (event: any) => void;
    onPipelineComplete?: (event: {
        plans_processed: number; plans_ingested: number; plans_discovery_only: number;
        plans_skipped: number; plans_failed: number; total_bindings_discovered: number;
    }) => void;
    onResult?: (result: any) => void;
    onError?: (error: string) => void;
}

/** Consume one property-analysis SSE stream.
 *
 *  Extracted at Stage 7.5 so the resume endpoint reuses this consumer instead
 *  of carrying a second copy of the reader/decoder/line-buffer loop — two
 *  copies of a stream parser drift, and the events are identical by design.
 *  Unrecognised event types (e.g. the `resume_selection` preamble) fall to the
 *  default and are ignored unless they carry an `error`. */
function streamPipeline(
    url: string,
    request: unknown,
    callbacks: PipelineStreamCallbacks | undefined,
    failureMessage: string
): {abort: () => void} {
    const abortController = new AbortController();
    authFetch(url, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify(request),
        signal: abortController.signal,
    })
        .then(async response => {
            if (!response.ok) {
                // Surface the server's reason (e.g. the 404 "not stranded"
                // detail) rather than a bare status line.
                let detail = '';
                try {
                    detail = (await response.clone().json())?.detail || '';
                } catch { /* non-JSON body */ }
                throw new Error(detail || `HTTP ${response.status}: ${response.statusText}`);
            }
            if (!response.body) throw new Error('No response body');
            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';
            const processLine = (line: string) => {
                if (line.startsWith('data: ')) {
                    try {
                        const data = JSON.parse(line.slice(6));
                        switch (data.type) {
                            case 'progress': callbacks?.onProgress?.(data); break;
                            case 'plan_complete': callbacks?.onPlanComplete?.(data); break;
                            case 'plan_skipped': callbacks?.onPlanSkipped?.(data); break;
                            case 'plan_failed': callbacks?.onPlanFailed?.(data); break;
                            case 'pipeline_complete': callbacks?.onPipelineComplete?.(data); break;
                            case 'discovery_progress': callbacks?.onDiscoveryProgress?.(data); break;
                            case 'discovery_result': callbacks?.onDiscoveryResult?.(data); break;
                            case 'result': callbacks?.onResult?.(data); break;
                            default: if (data.error) callbacks?.onError?.(data.error);
                        }
                    } catch { /* ignore */ }
                }
            };
            const pump = (): Promise<void> => {
                return reader.read().then(({done, value}) => {
                    if (done) return;
                    buffer += decoder.decode(value, {stream: true});
                    const lines = buffer.split('\n');
                    buffer = lines.pop() || '';
                    for (const line of lines) { if (line.trim()) processLine(line); }
                    return pump();
                });
            };
            return pump();
        })
        .catch(error => {
            if (error.name !== 'AbortError') callbacks?.onError?.(error.message || failureMessage);
        });
    return {abort: () => abortController.abort()};
}

export interface AddressStats {
    total: number;
    kommuner: number;
}

export interface KommuneCoverage {
    kommune_code: string;
    navn: string | null;
    count: number;
}

export interface AddressCoverage {
    rows: KommuneCoverage[];
}

export interface AddressRefreshArea {
    code: string;
    name: string;
    status: string;
    inserted: number;
    error: string | null;
}

export interface AddressRefreshJob {
    job_id: string;
    status: string; // pending | processing | completed | completed_with_errors | failed
    inserted: number;
    updated: number;
    skipped: number;
    error: string | null;
    national: boolean;
    fylker_total: number | null;
    fylker_done: number | null;
    fylker_failed: number | null;
    current_area: string | null;
    areas: AddressRefreshArea[] | null;
}

// --- Plan popularity index (Phase 9) ---

export interface PlanIndexArea {
    kommune_code: string;
    status: string; // running | completed | partial | failed
    phase: string;  // harvest | tally
    plans_found: number;
    pieces_stored: number;
    wms_queries: number;
    addresses_total: number;
    addresses_covered: number;
    error: string | null;
    gaarder_total?: number;
    gaarder_failed?: number;
    fetch_failures?: number;
    circuit_broken?: boolean;
    budget_exhausted?: boolean;
}

export interface PlanIndexJob {
    job_id: string;
    status: string; // pending | processing | completed | completed_with_errors | stopped | failed
    // Kommuner CLAIMED by this job up front (LCF Phase 12.1) — drives the
    // per-kommune disable of «Indekser kommune»; areas[] only lists started.
    kommuner: string[];
    kommuner_total: number;
    kommuner_done: number;
    kommuner_failed: number;
    current_kommune: string | null;
    started_at: string | null;
    areas: PlanIndexArea[];
    error: string | null;
}

export interface PlanIndexKommuneStatus {
    kommune_code: string;
    status: string; // pending | harvesting | tallying | completed | partial | failed
    harvested_at: string | null;
    tallied_at: string | null;
    plans_found: number;
    plans_total: number | null;
    pieces_stored: number;
    wms_queries: number;
    addresses_total: number;
    addresses_covered: number;
    error: string | null;
    updated_at: string | null;
}

export interface PlanIndexStats {
    kommuner: PlanIndexKommuneStatus[];
    totals: {
        kommuner_indexed: number;
        plans_found: number;
        pieces_stored: number;
        wms_queries: number;
    };
    kdp_indexed: boolean;
}

export interface PlanPopularityRow {
    plan_id: string;
    plannavn: string | null;
    plantype: number | null;
    plantype_name: string | null;  // SB-273 — served from the shared plantype table; null when plantype is
    planstatus: number | null;
    ikraft: number | null;
    address_count: number;
    geometry_source: string | null;
    register_url: string | null;   // plan page at arealplaner.no
    pdf_planid: number | null;     // id for /api/location/plans/{kommune}/{id}/pdf; null = no direct PDF path
    computed_at: string | null;
}

export interface PlanIndexKommune {
    kommune_code: string;
    navn: string;
    addresses_with_coords: number;
    status: string | null;      // null = never indexed
    tallied_at: string | null;
}

export interface PlanIndexPopularity {
    kommune_code: string;
    kommune_status: PlanIndexKommuneStatus | null;
    plans: PlanPopularityRow[];
}

export function createLocationApi(client: AxiosInstance) {
    return {
        // Location API
        async searchAddresses(query: string, kommune?: string, limit: number = 10): Promise<AddressSearchResult[]> {
            const params: Record<string, any> = {q: query, limit};
            if (kommune) params.kommune = kommune;
            const response = await client.get('/api/location/addresses/search', {params});
            return response.data;
        },
        async fetchPropertyContext(gnr: number, bnr: number, kommuneCode: string, skipCases: boolean = false, skipCache: boolean = false): Promise<PropertyContext> {
            const response = await client.post('/api/location/context/fetch', {
                gnr, bnr, kommune_code: kommuneCode, skip_cases: skipCases, skip_cache: skipCache,
            });
            return response.data;
        },
        async fetchPlanPdf(kommuneCode: string, planid: number): Promise<{
            status: 'ready' | 'downloading' | 'unavailable'; blob?: Blob; message?: string;
        }> {
            try {
                const response = await client.get(`/api/location/plans/${kommuneCode}/${planid}/pdf`, {responseType: 'blob'});
                return {status: 'ready', blob: response.data};
            } catch (error: any) {
                if (error.response?.status === 202) {
                    const data = await error.response.data.text();
                    const json = JSON.parse(data);
                    return {status: 'downloading', message: json.message};
                }
                if (error.response?.status === 404) {
                    return {status: 'unavailable', message: 'PDF not available'};
                }
                throw error;
            }
        },
        /** SB-148 — a plan's register documents; with `sha256`, which of them the
         *  document we hold IS. Fires a download on the LCF side when a hash is
         *  given, so call it on demand — never while rendering a picker list. */
        async fetchPlanDocuments(
            kommuneCode: string, planid: number, sha256?: string,
        ): Promise<PlanDocuments> {
            const response = await client.get(
                `/api/location/plans/${kommuneCode}/${planid}/documents`,
                {params: sha256 ? {sha256} : undefined},
            );
            return response.data;
        },
        async fetchPlanMetadata(kommuneCode: string, planid: number): Promise<{
            status: 'ready' | 'unavailable' | 'error'; metadata?: PlanMetadata; message?: string;
        }> {
            try {
                const response = await client.get(`/api/location/plans/${kommuneCode}/${planid}/metadata`);
                return {status: 'ready', metadata: response.data};
            } catch (error: any) {
                if (error.response?.status === 404) {
                    return {status: 'unavailable', message: error.response.data?.detail || 'PDF not available'};
                }
                if (error.response?.status === 500) {
                    return {status: 'error', message: error.response.data?.detail || 'Extraction failed'};
                }
                throw error;
            }
        },

        // Plans API (Plan-Document Linking)
        async getPlanLinkStatusBatch(kommuneCode: string, plannavns: string[]): Promise<{
            links: Array<{
                plannavn: string; kommune_code: string; linked: boolean;
                status: 'not_linked' | 'extracting' | 'extracted' | 'failed' | 'linked';
                doc_id: string | null; doc_title: string | null;
                pending_doc_id: string | null; scanned: boolean;
                // Stage 7.5 — extracted but never ingested; resume, don't approve.
                resumable?: boolean;
            }>;
        }> {
            const response = await client.get('/api/plans/link-status', {
                params: {kommune_code: kommuneCode, plannavns: plannavns.join(',')},
            });
            return response.data;
        },
        async getPlanLinkStatus(kommuneCode: string, plannavn: string): Promise<{
            plannavn: string; kommune_code: string; linked: boolean;
            status: 'not_linked' | 'extracting' | 'extracted' | 'failed' | 'linked';
            doc_id: string | null; doc_title: string | null;
            pending_doc_id: string | null; scanned: boolean;
            resumable?: boolean;
        }> {
            const response = await client.get(`/api/plans/${encodeURIComponent(plannavn)}/link-status`, {
                params: {kommune_code: kommuneCode},
            });
            return response.data;
        },
        async updatePlanLink(kommuneCode: string, plannavn: string, docId: string): Promise<{
            plannavn: string; kommune_code: string; linked: boolean;
            status: 'not_linked' | 'extracting' | 'extracted' | 'linked';
            doc_id: string | null; doc_title: string | null; pending_doc_id: string | null;
        }> {
            const response = await client.put(`/api/plans/${encodeURIComponent(plannavn)}/link`, {doc_id: docId}, {
                params: {kommune_code: kommuneCode},
            });
            return response.data;
        },
        async deletePlanLink(kommuneCode: string, plannavn: string): Promise<void> {
            await client.delete(`/api/plans/${encodeURIComponent(plannavn)}/link`, {
                params: {kommune_code: kommuneCode},
            });
        },
        /** SB-149 — scoped to the kommune, and to non-national documents. This feeds
         *  the control that writes `plan_doc_link.reg_doc_id`, i.e. PLAN_TIER, so what
         *  it offers can change what governs a property. It used to return every
         *  document in the corpus (67 on prod; 6 for Færder once scoped). */
        async getAvailableDocsForLinking(
            kommuneCode: string,
        ): Promise<{docs: Array<{doc_id: string; title: string}>}> {
            const response = await client.get('/api/plans/docs', {
                params: {kommune_code: kommuneCode},
            });
            return response.data;
        },
        /** SB-144 — the kommune's register plans, for the planid picker at manual
         *  ingest. Offered rather than typed: a planid typed by hand is a name-match
         *  with extra steps. An empty list is a real answer («no register plan is
         *  known for this kommune yet») and the field stays optional. */
        async getRegisterPlans(kommuneCode: string): Promise<{
            kommune_code: string;
            plans: Array<{
                planid: number; plannavn: string; doc_id: string | null; status: string;
                /** SB-147 — what the REGISTER says, so the choice is decidable.
                 *  null = not learned yet (no property in the kommune looked up),
                 *  never «the plan has no type». Render «—», not a blank. */
                plantype: number | null; plantype_name: string | null;
                ikraft: number | null; national_plan_id: string | null;
            }>;
        }> {
            const response = await client.get('/api/plans/register', {
                params: {kommune_code: kommuneCode},
            });
            return response.data;
        },
        async startPlanExtraction(kommuneCode: string, plannavn: string, pendingDocId: string): Promise<{
            plannavn: string; kommune_code: string; linked: boolean;
            status: 'not_linked' | 'extracting' | 'extracted' | 'linked'; pending_doc_id: string | null;
        }> {
            const response = await client.post(`/api/plans/${encodeURIComponent(plannavn)}/start-extraction`, {
                pending_doc_id: pendingDocId,
            }, {params: {kommune_code: kommuneCode}});
            return response.data;
        },

        // Property Analysis Pipeline
        analyzePropertyStream(
            request: {
                kommune_code: string; gnr: number; bnr: number;
                plans: Array<{planid: number; plannavn: string; pdf_available: boolean}>;
                skip_discovery?: boolean;
                // SB-77: false — discovered bindings wait for review. The backend
                // default is false too; the dialogs send it so the intent is visible.
                auto_approve?: boolean;
            },
            callbacks?: {
                onProgress?: (event: any) => void;
                onPlanComplete?: (event: {plannavn: string; doc_id: string}) => void;
                onPlanSkipped?: (event: {plannavn: string; doc_id: string | null; reason: string}) => void;
                onPlanFailed?: (event: {plannavn: string; error: string}) => void;
                onDiscoveryProgress?: (event: any) => void;
                onDiscoveryResult?: (event: any) => void;
                onPipelineComplete?: (event: {
                    plans_processed: number; plans_ingested: number; plans_discovery_only: number;
                    plans_skipped: number; plans_failed: number; total_bindings_discovered: number;
                }) => void;
                onResult?: (result: any) => void;
                onError?: (error: string) => void;
            }
        ): {abort: () => void} {
            return streamPipeline('/api/property-analysis/analyze-stream', request, callbacks, 'Analysis stream failed');
        },

        /** Stage 7.5 — finish a plan whose ingest never ran.
         *
         *  Same pipeline Phase C as a normal run (auto-config → validate → ingest →
         *  link → discover) and therefore the same SSE events, so it
         *  shares the analyze stream's consumer. 404s unless the plan really is
         *  stranded — the endpoint can only finish abandoned work, never
         *  re-ingest a healthy or already-linked plan. */
        resumeStrandedPlanStream(
            request: {kommune_code: string; plannavn: string; skip_discovery?: boolean},
            callbacks?: PipelineStreamCallbacks
        ): {abort: () => void} {
            return streamPipeline('/api/property-analysis/resume-stranded-stream', request, callbacks, 'Resume stream failed');
        },

        /** Stage 7.5 — plans extracted but never ingested, awaiting resume. */
        async listStrandedPlans(): Promise<{
            links: Array<{
                kommune_code: string; plannavn: string; planid: number | null;
                pending_doc_id: string; source_filename: string | null;
                chapters: number; extraction_completed_at: string | null;
                updated_at: string | null;
            }>;
            count: number;
        }> {
            const response = await client.get('/api/plans/stranded');
            return response.data;
        },

        // Health Check
        async health() {
            const response = await client.get('/health');
            return response.data;
        },

        // --- Admin: address-registry sync (national Geonorge refresh) ---
        async getAddressStats(): Promise<AddressStats> {
            const response = await client.get('/api/location/addresses/stats');
            return response.data;
        },
        async getAddressCoverage(): Promise<AddressCoverage> {
            const response = await client.get('/api/location/addresses/coverage');
            return response.data;
        },
        async triggerNationalAddressRefresh(): Promise<{status: string; job_id: string; message: string}> {
            const response = await client.post('/api/location/addresses/refresh', {national: true});
            return response.data;
        },
        async getAddressRefreshJob(jobId: string): Promise<AddressRefreshJob> {
            const response = await client.get(`/api/location/addresses/refresh/${jobId}`);
            return response.data;
        },

        // --- Admin: plan popularity index (Phase 9) ---
        async triggerPlanIndexRefresh(target: {kommune_code?: string; all_covered?: boolean}): Promise<{status: string; job_id: string; message: string}> {
            const response = await client.post('/api/location/plan-index/refresh', target);
            return response.data;
        },
        async getPlanIndexJob(jobId: string): Promise<PlanIndexJob> {
            const response = await client.get(`/api/location/plan-index/jobs/${jobId}`);
            return response.data;
        },
        async getPlanIndexActiveJobs(): Promise<{jobs: PlanIndexJob[]}> {
            const response = await client.get('/api/location/plan-index/active-jobs');
            return response.data;
        },
        async stopPlanIndexJob(jobId: string): Promise<PlanIndexJob> {
            const response = await client.post(`/api/location/plan-index/jobs/${jobId}/stop`);
            return response.data;
        },
        async getPlanIndexStats(): Promise<PlanIndexStats> {
            const response = await client.get('/api/location/plan-index/stats');
            return response.data;
        },
        async getPlanIndexKommuner(): Promise<{kommuner: PlanIndexKommune[]}> {
            const response = await client.get('/api/location/plan-index/kommuner');
            return response.data;
        },
        async getPlanIndexPopularity(kommuneCode: string, limit: number = 50, onlyVedtatt: boolean = true): Promise<PlanIndexPopularity> {
            const response = await client.get('/api/location/plan-index/popularity', {
                params: {kommune_code: kommuneCode, limit, only_vedtatt: onlyVedtatt},
            });
            return response.data;
        },
    };
}
